import { useLayoutEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

// Route changes otherwise retain the footer's scroll position in BrowserRouter.
// Keep in-page anchors and browser Back/Forward restoration intact.
export default function ScrollToTop() {
  const location = useLocation();
  const navigationType = useNavigationType();
  const previousPath = useRef(null);

  useLayoutEffect(() => {
    const firstRender = previousPath.current === null;
    const changedPage = previousPath.current !== location.pathname;
    previousPath.current = location.pathname;
    if (location.hash || (!firstRender && navigationType === "POP")) return;
    if (!changedPage && !location.state?.scrollToTop) return;

    // The website has a global smooth !important rule. Temporarily override it
    // so a new page opens at the top immediately, without animating from below.
    const style = document.documentElement.style;
    const value = style.getPropertyValue("scroll-behavior");
    const priority = style.getPropertyPriority("scroll-behavior");
    style.setProperty("scroll-behavior", "auto", "important");
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    if (value) style.setProperty("scroll-behavior", value, priority);
    else style.removeProperty("scroll-behavior");
    // A same-page footer link remains focused after clicking. Reset once more
    // after the browser finishes its click/focus scroll for that frame.
    const frame = requestAnimationFrame(() =>
      window.scrollTo({ top: 0, left: 0, behavior: "instant" }),
    );
    return () => cancelAnimationFrame(frame);
  }, [
    location.key,
    location.pathname,
    location.hash,
    location.state,
    navigationType,
  ]);

  return null;
}
